import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { InfoTooltip } from '../agedInventory/InfoTooltip.component';
import {
  SETTINGS_CONFIG,
  SETTING_SOURCE_LABELS,
  clampSetting,
  formatSettingNumber,
  resolveVendorTerms,
  settingConfig,
  statusConfig,
} from './coreReplenishment.logic';
import type { ResolvedSettings } from './coreReplenishment.logic';
import type { NumericSettingKey, ReplenishmentSettings, ReplenishmentStatus, SettingSection, SettingSource } from './coreReplenishment.types';

const SECTIONS: readonly SettingSection[] = ['Order timing', 'Cover', 'Velocity', 'Status', 'Core items'];

const SOURCE_TAG_CLASS: Record<SettingSource, string> = {
  default: 'bg-[#F2F4F7] text-[#475467] border-[#EAECF0]',
  client: 'bg-white text-[#344054] border-[#D0D5DD]',
  plan: 'bg-[#F4F0FF] text-[#6941C6] border-[#D6BBFB]',
  user: 'bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]',
};

// Status thresholds are labelled with the status pill the table shows.
const LABEL_STATUS: Partial<Record<NumericSettingKey, ReplenishmentStatus>> = {
  orderNowMultiplier: 'order_now',
  nextOrderMultiplier: 'next_order',
};

const STATUS_MULTIPLIER_NOTE = '× (lead time + cadence)';

// Fixed-height buttons must never wrap: a second line would spill outside the border.
const FOOTER_BUTTON_CLASS =
  'h-[32px] px-2.5 shrink-0 whitespace-nowrap rounded-[8px] text-[12px] font-semibold border border-[#D0D5DD] bg-white text-[#344054] hover:bg-[#F9FAFB] shadow-2xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

// The nearest ancestor that scrolls (the report modal's content area); null when the page scrolls.
function scrollParentOf(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

/**
 * How tall the sidebar can be with its footer still on screen: from its top (or the top of the
 * scroll area, once it sticks there) to the bottom of the scroll area, which inside the report
 * modal ends at the viewport and starts below the modal's fixed close bar. Re-measured on scroll
 * and resize; null until measured, leaving the CSS fallback in place.
 */
function useVisibleHeight(ref: React.RefObject<HTMLElement>, active: boolean): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return undefined;
    const parent = scrollParentOf(el);
    const measure = () => {
      const area = parent ? parent.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      const visible = Math.floor(area.bottom - Math.max(el.getBoundingClientRect().top, area.top));
      setHeight(visible > 0 ? visible : null);
    };
    measure();
    const scroller: HTMLElement | Window = parent ?? window;
    scroller.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer = parent && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (parent) observer?.observe(parent);
    return () => {
      scroller.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [ref, active]);
  return height;
}

const UNIT_LABEL: Record<string, string> = { wks: 'wks', days: 'days', '×': '×', '%': '%', units: 'units' };

export const SourceTag: React.FC<{ source: SettingSource }> = ({ source }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap ${SOURCE_TAG_CLASS[source]}`}>
    {SETTING_SOURCE_LABELS[source]}
  </span>
);

const tooltipText = (key: NumericSettingKey): string => {
  const { description, min, max, unit } = settingConfig(key);
  return `${description} Allowed: ${formatSettingNumber(min)} to ${formatSettingNumber(max)} ${UNIT_LABEL[unit]}.`;
};

interface NumberSettingInputProps {
  settingKey: NumericSettingKey;
  value: number;
  error?: string;
  onCommit: (value: number) => void;
  // Inside a sentence ("9 of last 12 wks"), the unit is written once after the inputs.
  hideUnit?: boolean;
}

/** Commits on blur or Enter, so half-typed values never recalculate the table. */
const NumberSettingInput: React.FC<NumberSettingInputProps> = ({ settingKey, value, error, onCommit, hideUnit = false }) => {
  const config = settingConfig(settingKey);
  const [text, setText] = useState(formatSettingNumber(value));
  const [rangeError, setRangeError] = useState<string | null>(null);
  useEffect(() => {
    setText(formatSettingNumber(value));
    setRangeError(null);
  }, [value]);

  const commit = () => {
    const parsed = Number(text.trim());
    if (text.trim() === '' || !Number.isFinite(parsed) || parsed < config.min || parsed > config.max) {
      setRangeError(`Enter ${config.min} to ${config.max}.`);
      return;
    }
    setRangeError(null);
    const next = clampSetting(settingKey, parsed);
    setText(formatSettingNumber(next));
    if (next !== value) onCommit(next);
  };

  const shownError = rangeError ?? error;
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div
        className={`h-[30px] ${hideUnit ? 'w-[44px]' : 'w-[76px]'} flex items-center gap-1 bg-white border rounded-[8px] px-2 shadow-2xs focus-within:ring-2 focus-within:ring-[#7F56D9] ${
          shownError ? 'border-[#FDA29B]' : 'border-[#D0D5DD]'
        }`}
      >
        <input
          type="text"
          inputMode="decimal"
          value={text}
          aria-label={config.label}
          aria-invalid={Boolean(shownError)}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setText(formatSettingNumber(value));
          }}
          className="w-full min-w-0 text-[13px] font-semibold text-[#344054] text-right focus:outline-none bg-transparent"
        />
        {!hideUnit && <span className="text-[12px] text-[#667085] shrink-0">{UNIT_LABEL[config.unit]}</span>}
      </div>
      {shownError && (
        <span role="alert" className="text-[11px] text-[#B42318] whitespace-nowrap">
          {shownError}
        </span>
      )}
    </div>
  );
};

interface RowShellProps {
  label: string;
  // Shows the label as this status's pill, as the table's Status column does.
  labelStatus?: ReplenishmentStatus;
  subtext: string;
  tooltip: string;
  source: SettingSource;
  note?: string;
  // Label and subtext across the full width, with the source tag and control on a line below.
  stacked?: boolean;
  children: React.ReactNode;
}

/** Label and tooltip over a short line on the left; source tag and control on the right. */
const RowShell: React.FC<RowShellProps> = ({ label, labelStatus, subtext, tooltip, source, note, stacked = false, children }) => {
  const heading = (
    <div className="flex flex-col min-w-0 gap-0.5">
      <div className="flex items-center">
        {labelStatus ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold leading-[16px] whitespace-nowrap ${statusConfig(labelStatus).badgeClass}`}>
            {label}
          </span>
        ) : (
          <span className="text-[13px] font-semibold leading-[18px] text-[#344054]">{label}</span>
        )}
        <InfoTooltip text={tooltip} position="bottom" align="left" />
      </div>
      <span className="text-[12px] text-[#71717A] leading-[16px]">{subtext}</span>
    </div>
  );

  if (stacked) {
    return (
      <div className="flex flex-col gap-2 py-2.5">
        {heading}
        <div className="flex items-center justify-between gap-3">
          <SourceTag source={source} />
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      {heading}
      <div className="shrink-0 flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <SourceTag source={source} />
          {children}
        </div>
        {note && <span className="text-[11px] text-[#667085] whitespace-nowrap">{note}</span>}
      </div>
    </div>
  );
};

export interface CoreReplenishmentSettingsProps {
  open: boolean;
  onClose: () => void;
  resolved: ResolvedSettings;
  wosOverride: number | null;
  // The plan's cover over the products shown ("4.6", or a range when classes differ), null when no
  // shown class has one; and the applied target (cover × uplift), null with no products.
  planCover: string | null;
  planTarget: string | null;
  vendors: string[];
  // Edits apply to the table right away.
  onChange: (changes: Partial<ReplenishmentSettings>) => void;
  onResetToClientDefaults: () => void;
  onSaveToView: () => void;
  // Saves client-level changes for everyone at the client.
  onApply: () => void;
  hasClientChanges: boolean;
  applying?: boolean;
  applyError?: string | null;
}

/** Docked "Report settings" panel. */
export const CoreReplenishmentSettings: React.FC<CoreReplenishmentSettingsProps> = ({
  open,
  onClose,
  resolved,
  wosOverride,
  planCover,
  planTarget,
  vendors,
  onChange,
  onResetToClientDefaults,
  onSaveToView,
  onApply,
  hasClientChanges,
  applying = false,
  applyError = null,
}) => {
  const panelRef = useRef<HTMLElement>(null);
  const visibleHeight = useVisibleHeight(panelRef, open);
  if (!open) return null;
  const { requested, sources, errors } = resolved;
  const hasErrors = Object.keys(errors).length > 0;
  const terms = resolveVendorTerms(requested);
  const noPlanCover = formatSettingNumber(terms.lead_time_weeks + terms.cadence_weeks);

  const numberRow = (key: NumericSettingKey) => {
    const config = settingConfig(key);
    return (
      <RowShell
        key={key}
        label={config.label}
        labelStatus={LABEL_STATUS[key]}
        subtext={config.subtext}
        tooltip={tooltipText(key)}
        source={sources[key]}
        note={config.section === 'Status' ? STATUS_MULTIPLIER_NOTE : undefined}
      >
        <NumberSettingInput settingKey={key} value={requested[key]} error={errors[key]} onCommit={(value) => onChange({ [key]: value })} />
      </RowShell>
    );
  };

  const appliedTargetNote =
    wosOverride !== null
      ? `Applied target: ${wosOverride} wks, from the Weeks of supply target field. Clear it to return to cover × uplift.`
      : `Applied target: ${planTarget ?? '—'} wks (cover × uplift). Classes with no plan use a default of ${noPlanCover} wks.`;

  const sectionRows = (section: SettingSection): React.ReactNode => {
    if (section === 'Cover') {
      return (
        <>
          <RowShell
            label="Target cover"
            subtext="Plan inventory ÷ plan weekly sales"
            tooltip={`Weeks of supply the class plan carries: next month's planned inventory ÷ its planned weekly sales. Read-only. Without a plan, lead time + cadence (${noPlanCover} wks) is used.`}
            source={planCover === null ? 'default' : 'plan'}
          >
            <span className="h-[30px] min-w-[76px] flex items-center justify-end gap-1 px-2 rounded-[8px] bg-[#F9FAFB] border border-[#EAECF0] text-[13px] font-semibold text-[#344054]">
              {planCover ?? noPlanCover}
              <span className="text-[12px] font-normal text-[#667085]">wks</span>
            </span>
          </RowShell>
          {numberRow('coreUplift')}
          <p className="my-2 rounded-lg bg-[#F9FAFB] border border-[#EAECF0] px-3 py-2 text-[12px] text-[#475467]" data-testid="applied-target-note">
            {appliedTargetNote}
          </p>
        </>
      );
    }
    if (section === 'Core items') {
      return (
        <>
          <RowShell
            stacked
            label="Detect core items"
            subtext={settingConfig('coreSoldWeeks').subtext}
            tooltip={`An item is core when it sold at least ${formatSettingNumber(requested.minUnitsPerWeek)} ${
              requested.minUnitsPerWeek === 1 ? 'unit' : 'units'
            } in N of the last M weeks. ${tooltipText('coreSoldWeeks')} M: ${formatSettingNumber(settingConfig('coreWindowWeeks').min)} to ${formatSettingNumber(
              settingConfig('coreWindowWeeks').max
            )} wks.`}
            source={sources.coreSoldWeeks === 'user' || sources.coreWindowWeeks === 'user' ? 'user' : sources.coreSoldWeeks}
          >
            <div className="flex items-start gap-1.5">
              <NumberSettingInput hideUnit settingKey="coreSoldWeeks" value={requested.coreSoldWeeks} error={errors.coreSoldWeeks} onCommit={(value) => onChange({ coreSoldWeeks: value })} />
              <span className="h-[30px] flex items-center text-[12px] text-[#667085] whitespace-nowrap">of last</span>
              <NumberSettingInput settingKey="coreWindowWeeks" value={requested.coreWindowWeeks} error={errors.coreWindowWeeks} onCommit={(value) => onChange({ coreWindowWeeks: value })} />
            </div>
          </RowShell>
        </>
      );
    }
    return SETTINGS_CONFIG.filter((c) => c.section === section).map((c) => numberRow(c.key));
  };

  return (
    <aside
      ref={panelRef}
      aria-label="Report settings"
      style={visibleHeight === null ? undefined : { height: visibleHeight }}
      className="w-[400px] shrink-0 self-start sticky top-0 h-screen flex flex-col bg-white border-l border-[#E4E4E7] overflow-hidden"
    >
      <div className="shrink-0 flex items-start justify-between gap-2 px-4 py-3 border-b border-[#EAECF0]">
        <div>
          <h2 className="text-[15px] font-bold text-[#101828]">Report settings</h2>
          <p className="text-[12px] text-[#71717A]">Changes recalculate the table right away.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close report settings" className="p-1 text-[#98A2B3] hover:text-[#344054] cursor-pointer">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        {SECTIONS.map((section) => (
          <section key={section} className="py-2 border-b border-[#F2F4F7]">
            <h3 className="pt-2 text-[11px] font-semibold uppercase tracking-wide text-[#71717A]">{section}</h3>
            <div className="divide-y divide-[#F2F4F7]">{sectionRows(section)}</div>
          </section>
        ))}

        <section className="py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#71717A]">Vendor overrides</h3>
          <table className="w-full mt-2 text-[12px]">
            <thead>
              <tr className="text-left text-[#667085] border-b border-[#EAECF0]">
                <th className="py-1.5 font-semibold">Vendor</th>
                <th className="py-1.5 font-semibold text-right">Lead</th>
                <th className="py-1.5 font-semibold text-right">Cadence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F2F4F7]">
              {vendors.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-2 text-[#98A2B3] italic">
                    No vendors in this report.
                  </td>
                </tr>
              )}
              {/* No per-vendor terms yet, so every vendor reads the client's lead time and cadence. */}
              {vendors.map((vendor) => (
                <tr key={vendor} className="text-[#344054]">
                  <td className="py-1.5 pr-2 font-medium truncate max-w-[200px]" title={vendor}>
                    {vendor}
                  </td>
                  <td className="py-1.5 pl-2 text-right whitespace-nowrap">{formatSettingNumber(terms.lead_time_weeks)} wks</td>
                  <td className="py-1.5 pl-2 text-right whitespace-nowrap">{formatSettingNumber(terms.cadence_weeks)} wks</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="shrink-0 border-t border-[#EAECF0] px-4 py-3 flex flex-col gap-2">
        {hasClientChanges && (
          <p className="text-[12px] text-[#475467]">Apply saves lead time, order cadence and the core-item rule for everyone at the client.</p>
        )}
        {applyError && (
          <p role="alert" className="text-[12px] text-[#B42318]">
            {applyError}
          </p>
        )}
        <div className="flex items-center justify-between gap-3">
          {/* One line always: the text link truncates before either button gives up any width. */}
          <button
            type="button"
            onClick={onResetToClientDefaults}
            title="Reset to client defaults"
            className="min-w-0 truncate whitespace-nowrap text-left text-[12px] font-medium text-[#667085] hover:text-[#101828] cursor-pointer"
          >
            Reset to client defaults
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={onSaveToView} disabled={hasErrors} className={FOOTER_BUTTON_CLASS}>
              Save to this view
            </button>
            <button
              type="button"
              onClick={onApply}
              disabled={hasErrors || applying}
              className="h-[32px] px-3.5 shrink-0 whitespace-nowrap rounded-[8px] text-[12px] font-bold bg-[#7F56D9] hover:bg-[#6941C6] text-white shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default CoreReplenishmentSettings;
